import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useCmsApi } from '@/services/cms-api'
import {
  Column,
  PackageInfo,
  Row,
  ServiceContent,
  ServiceList
} from '@/services/cms-api/service-menu/types'
import { resolveImageUrl } from '@/utils/constants'
import { isNil } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import { FunctionComponent, useEffect, useState } from 'react'
import ServiceContentRenderer from '../../components/page/service/service-content'
import PackageInfoRenderer from '../../components/page/service/package-info'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import RowServiceContentRenderer from '@/components/page/service/row-content'
import ColServiceContentRenderer from '@/components/page/service/col-content'

const ServicePage: FunctionComponent = () => {
  const { locale } = useLanguage()
  const router = useRouter()
  const { getServiceList } = useCmsApi()
  const { showError } = useToast()
  const { translate } = useIntl()
  const [data, setData] = useState<ServiceList | undefined>()
  const [selectedService, setSelectedService] = useState<number | undefined>(0)
  const [clicked, setClicked] = useState<number | undefined>(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const id = router.query.id
    if (id !== null && id !== undefined && typeof id === 'string') {
      void fetchData(id)
    }
  }, [router.query.id])

  const fetchData = async (id: any): Promise<void> => {
    try {
      const res = await getServiceList(id)
      if (res !== undefined && res?.services?.length > 0) {
        setSelectedService(0)
        setClicked(0)
        setData(res)
      } else {
        showError(translate('noResult'))
        await router.push('/')
      }
    } catch (e) {
      showError(translate('error'))
    } finally {
      setLoading(false)
    }
  }
  if (loading) {
    return (
      <div className='flex w-full h-screen justify-center items-center'>
        <Loader />
      </div>
    )
  }

  const handleOpen = (): void => {
    // Check if the services array has at least one item and that item has a link property
    if (data?.services[selectedService ?? 0]?.link !== null) {
      window.open(data?.services[selectedService ?? 0]?.link ?? '', '_blank') // Opens the link in a new tab
    } else {
      console.error('Link not found')
    }
  }

  return (
    <div className='w-full flex justify-center'>
      <div className='bg-surfaceInverse w-[95vw] rounded-2xl lg:p-14 md:p-8 p-6 flex md:flex-row justify-between flex-col-reverse'>
        <div className='flex flex-col 2xl:gap-5 xl:gap-5 lg:gap-5 gap-5 mt-8 md:mt-0 xl:w-2/3 md:w-3/5'>
          <div
            className='rounded-xl bg-cover bg-center w-full h-[312px]'
            style={{
              backgroundImage: `url(${
                resolveImageUrl((data?.services[selectedService ?? 0]?.coverImage ?? ''))
              })`
            }}
          >
            <div className='h-full w-full bg-gradient-to-t from-black/80 via-black/60 to-black/80 xl:from-black/60 rounded-xl p-14 flex flex-col justify-end'>
              <div className='flex flex-col xl:flex-row items-start xl:items-center xl:justify-between'>
                <div className='w-full'>
                  <span className='text-textAdditional md:text-4xl text-xl font-extrabold'>
                    {locale === 'en'
                      ? data?.services[selectedService ?? 0]?.titleEn ?? ''
                      : data?.services[selectedService ?? 0]?.titleMn ?? ''}
                  </span>
                  <div className='flex flex-col md:flex-row md:justify-between justify-start md:items-center items-start'>
                    <p className='text-textAdditional text-sm md:text-base font-medium mt-4 w-full md:w-2/3'>
                      {locale === 'en'
                        ? data?.services[selectedService ?? 0]?.descriptionEn ??
                          ''
                        : data?.services[selectedService ?? 0]?.descriptionMn ??
                          ''}
                    </p>
                    {data?.services[selectedService ?? 0]?.link !== null
                      ? (
                        <button
                          onClick={handleOpen}
                          className='bg-mainbg hover:bg-secondarySoftMain rounded-xl text-black text-center p-3 w-[167px] mt-4 xl:mt-0'
                        >
                          {locale === 'en'
                            ? data?.services[selectedService ?? 0]?.buttonEn ?? translate('service.serviceButton')
                            : data?.services[selectedService ?? 0]?.buttonMn ?? translate('service.serviceButton')}
                        </button>
                        )
                      : (
                        <></>
                        )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {data?.services !== undefined &&
            data?.services[selectedService ?? 0].contents !== undefined &&
            data?.services[selectedService ?? 0].contents?.map(
              (item: ServiceContent, idx: number) => (
                <ServiceContentRenderer content={item} idx={idx} key={idx} />
              )
            )}
          <div className='flex flex-col md:grid md:grid-cols-2 md:gap-4'>
            {data?.services !== undefined &&
              data?.services[selectedService ?? 0].row !== undefined &&
              data?.services[selectedService ?? 0].row?.map(
                (item: Row, idx: number) => (
                  <RowServiceContentRenderer
                    content={item}
                    idx={idx}
                    key={idx}
                  />
                )
              )}
          </div>
          <div className='flex flex-col'>
            {data?.services !== undefined &&
              data?.services[selectedService ?? 0].column !== undefined &&
              data?.services[selectedService ?? 0].column?.map(
                (item: Column, idx: number) => (
                  <ColServiceContentRenderer
                    content={item}
                    idx={idx}
                    key={idx}
                  />
                )
              )}
          </div>
          <div className='flex flex-col gap-10'>
            {data?.services[selectedService ?? 0].packageInfos !== undefined &&
            data?.services[selectedService ?? 0].packageInfos.length > 1
              ? (
                <div className='flex flex-col gap-10'>
                  {!isNil(
                    data.services[selectedService ?? 0].packageInfoHeaderEn
                  ) &&
                  !isNil(
                    data.services[selectedService ?? 0].packageInfoHeaderEn
                  ) && (
                    <div className='flex flex-col w-full items-center'>
                      <h1 className='font-bold md:text-4xl text-xl text-textHigh'>
                        {locale === 'en'
                          ? data.services[selectedService ?? 0]
                            .packageInfoHeaderEn
                          : data.services[selectedService ?? 0]
                            .packageInfoHeaderMn}
                      </h1>
                    </div>
                  )}

                  <ul className='bg-[#0000001A] border border-secondaryMidStroke items-center md:p-2 p-2 gap-4 rounded-2xl md:h-fit flex w-full overflow-auto'>
                    {data?.services[selectedService ?? 0].packageInfos?.map(
                      (item: PackageInfo, i: number) => (
                        <li
                          key={i}
                          className={classNames(
                            'w-full justify-center md:px-2 text-textHigh lg:px-5 p-2 rounded-xl font-semibold cursor-pointer hover:bg-secondarySoftMain flex items-center text-center text-sm',
                            i === clicked
                              ? 'bg-secondary text-textInverse'
                              : 'text-medium'
                          )}
                          onClick={() => setClicked(i)}
                        >
                          {locale === 'en'
                            ? item.nameEn ?? ''
                            : item.nameMn ?? ''}
                        </li>
                      )
                    )}
                  </ul>
                </div>
                )
              : (
                <></>
                )}
            {data?.services[selectedService ?? 0].packageInfos !==
              undefined && (
                <PackageInfoRenderer
                  data={
                  data?.services[selectedService ?? 0].packageInfos[
                    clicked ?? 0
                  ]
                }
                />
            )}
          </div>
        </div>
        <div className='md:border-l border-secondaryMidStroke md:pl-12 md:ml-12 xl:w-1/3 md:w-2/5'>
          <div className='sticky top-36'>
            <h1 className='font-bold text-2xl text-textHigh'>
              {locale === 'mn' ? data?.category.nameMn : data?.category.nameEn}
            </h1>
            <p className='mt-4 text-textMid text-sm font-medium'>
              {locale === 'mn'
                ? data?.category.descriptionMn
                : data?.category.descriptionEn}
            </p>
            <div className='flex flex-col gap-3 mt-14'>
              {data?.services.map((item, i) => (
                <DarkButton
                  key={i}
                  fullwidth
                  type={selectedService !== i ? 'outlined' : undefined}
                  size='sm'
                  onClick={() => setSelectedService(i)}
                >
                  {locale === 'mn' ? item.nameMn : item.nameEn}
                </DarkButton>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServicePage
